import { createDeltaSeriesStylingNew } from "@/constants/colorGenerator";
import type { PlotSerieStyle } from "@/types/data";
import type { DatetimesWithOffset } from "@/utility/time";
import type {
  ElementStyle,
  EnergyPlotCreate,
  EnergyPlotDataset,
  EnergyPlot as EnergyPlotSchema,
} from "@mm/metx-workbench.meteomatics.com";
import { DateTime } from "luxon";
import {
  ENERGY_FORECAST_VALUE_UNIT,
  type EnergyValueUnit,
  isEnergyValueUnit,
  normalizeEnergyPlotEnsMembers,
} from "./const";
import { normalizeSiteIdForEnergyApi, normalizeSiteIdsForEnergyApi } from "./utils";

export type EnergySeriesStyles = {
  actual?: PlotSerieStyle;
  power?: PlotSerieStyle;
  delta_1h?: PlotSerieStyle;
  delta_3h?: PlotSerieStyle;
  delta_6h?: PlotSerieStyle;
  delta_12h?: PlotSerieStyle;
  delta_24h?: PlotSerieStyle;
  combined_delta_1h?: PlotSerieStyle;
  combined_delta_3h?: PlotSerieStyle;
  combined_delta_6h?: PlotSerieStyle;
  combined_delta_12h?: PlotSerieStyle;
  combined_delta_24h?: PlotSerieStyle;
};

export type EnergyPlotGroup = {
  model: string;
  ensMembers?: string[];
  plotSeriesStyles: EnergySeriesStyles;
  actualRun?: EnergyPlotRun;
  previousRuns?: EnergyPlotRun[];
};

export type EnergyPlotRun = {
  initDate: number;
  plotSeriesStyles: EnergySeriesStyles;
  showDelta?: boolean;
  showRun?: boolean;
};

/** Deduplicates finite energy init dates and sorts them oldest to newest. */
export function normalizeEnergyInitDates(initDates: Array<number | null | undefined>): number[] {
  return Array.from(new Set(initDates.filter((initDate): initDate is number => Number.isFinite(initDate)))).sort(
    (left, right) => left - right,
  );
}

type EnergyInitDateAvailability =
  | Array<number | null | undefined>
  | {
      today?: Array<number | null | undefined>;
      yesterday?: Array<number | null | undefined>;
    }
  | null
  | undefined;

/** Accepts both raw and day-grouped availability payloads and returns one flat list. */
function flattenEnergyInitDateAvailability(initDates: EnergyInitDateAvailability): Array<number | null | undefined> {
  if (!initDates) {
    return [];
  }

  if (Array.isArray(initDates)) {
    return initDates;
  }

  return [...(initDates.yesterday ?? []), ...(initDates.today ?? [])];
}

/** Returns the newest available init dates, newest first. */
export function resolveLatestEnergyInitDates(initDates: EnergyInitDateAvailability, count: number): number[] {
  if (count <= 0) {
    return [];
  }

  return normalizeEnergyInitDates(flattenEnergyInitDateAvailability(initDates)).slice(-count).reverse();
}

/** Returns the newest available init date, or `undefined` when none exists. */
export function resolveLatestEnergyInitDate(initDates: EnergyInitDateAvailability): number | undefined {
  return resolveLatestEnergyInitDates(initDates, 1)[0];
}

/**
 * Builds the actual run while preserving existing per-run styles and
 * visibility when they already belong to the same init date.
 */
function createActualRun(group: EnergyPlotGroup, initDate: number): EnergyPlotRun {
  const existingActualRun = group.actualRun?.initDate === initDate ? group.actualRun : undefined;

  return {
    initDate,
    plotSeriesStyles: existingActualRun?.plotSeriesStyles ?? group.plotSeriesStyles,
    showRun: existingActualRun?.showRun,
    showDelta: existingActualRun?.showDelta,
  };
}

/**
 * Creates a styled previous-run entry for a deterministic model.
 * The index rotates through generated run and delta colors so multiple previous
 * runs remain visually distinct.
 */
export function createPreviousEnergyPlotRun(group: EnergyPlotGroup, initDate: number, index = 0): EnergyPlotRun {
  const defaultRunStyling = createDeltaSeriesStylingNew(group.plotSeriesStyles.power?.color);
  const runStyle = defaultRunStyling.run[index % defaultRunStyling.run.length];
  const deltaStyle = defaultRunStyling.delta[index % defaultRunStyling.delta.length];

  return {
    initDate,
    plotSeriesStyles: {
      power: runStyle,
      combined_delta_1h: deltaStyle,
    },
    showRun: true,
    showDelta: true,
  };
}

/**
 * Rebuilds previous runs from stored actual and previous init dates.
 * The active actual run is excluded, remaining runs are newest first, and
 * existing custom styles are preserved.
 */
function normalizePreviousRuns(group: EnergyPlotGroup, actualInitDate: number | undefined): EnergyPlotRun[] {
  const previousRunDates = normalizeEnergyInitDates([
    group.actualRun?.initDate,
    ...(group.previousRuns ?? []).map((run) => run.initDate),
  ])
    .filter((initDate) => initDate !== actualInitDate)
    .sort((left, right) => right - left);

  return previousRunDates.map((initDate, index) => {
    const existingRun = group.previousRuns?.find((run) => run.initDate === initDate);

    return existingRun
      ? {
          ...createPreviousEnergyPlotRun(group, initDate, index),
          ...existingRun,
          plotSeriesStyles: {
            ...createPreviousEnergyPlotRun(group, initDate, index).plotSeriesStyles,
            ...existingRun.plotSeriesStyles,
          },
        }
      : createPreviousEnergyPlotRun(group, initDate, index);
  });
}

/**
 * Normalizes deterministic run state around the latest available init date.
 * The latest run becomes `actualRun`; all older selected runs are represented
 * as `previousRuns` with stable styling metadata.
 */
export function normalizeEnergyPlotGroupRuns(
  group: EnergyPlotGroup,
  latestAvailableInitDate?: number,
): EnergyPlotGroup {
  const actualInitDate = latestAvailableInitDate ?? group.actualRun?.initDate;

  if (typeof actualInitDate !== "number" || !Number.isFinite(actualInitDate)) {
    return {
      ...group,
      previousRuns: normalizePreviousRuns(group, undefined),
    };
  }

  const actualRun = createActualRun(group, actualInitDate);
  const previousRuns = normalizePreviousRuns(group, actualInitDate);

  return {
    ...group,
    actualRun,
    previousRuns,
  };
}

/** Returns selected deterministic init dates in request order: actual run first, then previous runs. */
export function getSelectedEnergyPlotRunInitDates(group: EnergyPlotGroup): number[] {
  const normalizedGroup = normalizeEnergyPlotGroupRuns(group);

  if (!normalizedGroup.actualRun) {
    return [];
  }

  return [
    normalizedGroup.actualRun.initDate,
    ...(normalizedGroup.previousRuns ?? []).map((previousRun) => previousRun.initDate),
  ];
}

export type EnergyPlot = DatetimesWithOffset & {
  id: number;
  tabId: number;
  forecastType: string;
  siteId: string;
  subSiteIds?: string[];
  groups: EnergyPlotGroup[];
  title: string;
  titleStyle?: ElementStyle;
  legendStyle?: ElementStyle;
  timeLock: boolean;
  displayTimeVisible?: boolean;
  nowTimeVisible?: boolean;
  valueUnit?: EnergyValueUnit;
};

export const transformToEnergyPlot = (energyPlotSchema: EnergyPlotSchema): EnergyPlot => {
  const datasetGroups = transformToEnergyPlotGroup(energyPlotSchema.datasets);
  const normalizedSiteId = normalizeSiteIdForEnergyApi(energyPlotSchema.site_id, energyPlotSchema.forecast_type);
  const normalizedSubSiteIds = normalizeSiteIdsForEnergyApi(
    energyPlotSchema.sub_site_ids || undefined,
    energyPlotSchema.forecast_type,
  );
  return {
    id: energyPlotSchema.id,
    tabId: energyPlotSchema.id_tab,
    forecastType: energyPlotSchema.forecast_type,
    siteId: normalizedSiteId,
    subSiteIds: normalizedSubSiteIds,
    groups:
      datasetGroups.length > 0
        ? datasetGroups
        : [
            {
              model: energyPlotSchema.model || "invalid",
              plotSeriesStyles: energyPlotSchema.plotSeriesStyles,
              ensMembers: normalizeEnergyPlotEnsMembers({
                model: energyPlotSchema.model || "invalid",
                ensMembers: energyPlotSchema.ens_members || undefined,
              }),
            },
          ],
    title: energyPlotSchema.title || "Energy Plot",
    titleStyle: energyPlotSchema.titleStyle || undefined,
    legendStyle: energyPlotSchema.legendStyle || undefined,
    timeLock: !!energyPlotSchema.time_lock,
    datetimeFrom: energyPlotSchema.datetime_from,
    datetimeFromOffset: energyPlotSchema.datetime_from_offset,
    datetimeTo: energyPlotSchema.datetime_to,
    datetimeToOffset: energyPlotSchema.datetime_to_offset,
    displayTimeVisible: energyPlotSchema.display_time_plotline_visible || false,
    nowTimeVisible: energyPlotSchema.now_time_plotline_visible || false,
    valueUnit: isEnergyValueUnit(energyPlotSchema.unit) ? energyPlotSchema.unit : ENERGY_FORECAST_VALUE_UNIT,
  };
};

export const transformToEnergyPlotGroup = (
  datasets: Array<EnergyPlotDataset> | null | undefined,
): EnergyPlotGroup[] => {
  if (!datasets) {
    return [];
  }
  return datasets.map((dataset) => {
    return normalizeEnergyPlotGroupRuns({
      model: dataset.model,
      ensMembers: normalizeEnergyPlotEnsMembers({
        model: dataset.model,
        ensMembers: dataset.ens_members || undefined,
      }),
      plotSeriesStyles: dataset.plotSeriesStyles,
    });
  });
};

export const transformToEnergyPlotDataset = (groups: Array<EnergyPlotGroup>): EnergyPlotDataset[] => {
  if (!groups) {
    return [];
  }
  return groups.map((group) => {
    return {
      model: group.model,
      ens_members: group.ensMembers,
      plotSeriesStyles: group.plotSeriesStyles,
      init_date: null,
    };
  });
};

export const transformToEnergyPlotCreate = (energyPlot: EnergyPlot): EnergyPlotCreate => {
  return {
    ens_members: null,
    // This should be removed from the schema
    gridCellLayout: { gridColumnStart: 0, gridRowStart: 0, gridRowEnd: 0, gridColumnEnd: 0 },
    legendStyle: energyPlot.legendStyle || null,
    site_id: energyPlot.siteId,
    sub_site_ids: energyPlot.subSiteIds || null,
    forecast_type: energyPlot.forecastType,
    unit: energyPlot.valueUnit ?? ENERGY_FORECAST_VALUE_UNIT,
    datasets:
      energyPlot.groups?.map((group) => {
        return {
          model: group.model,
          plotSeriesStyles: group.plotSeriesStyles,
          init_date: null,
          ens_members: group.ensMembers,
        };
      }) || [],
    title: energyPlot.title || "Title",
    titleStyle: energyPlot.titleStyle ?? null,
    time_lock: energyPlot.timeLock,
    datetime_from: energyPlot.datetimeFrom,
    datetime_from_offset: energyPlot.datetimeFromOffset,
    datetime_to: energyPlot.datetimeTo,
    datetime_to_offset: energyPlot.datetimeToOffset,
    display_time_plotline_visible: energyPlot.displayTimeVisible,
    now_time_plotline_visible: energyPlot.nowTimeVisible,
  };
};

export const transformToEnergyPlotSchema = (energyPlot: EnergyPlot, profileId: number): EnergyPlotSchema => {
  return {
    id: energyPlot.id,
    id_profile: profileId,
    id_tab: energyPlot.tabId,
    model: energyPlot.groups[0]?.model || "",
    plotSeriesStyles: {},
    time_created: DateTime.now().toISO(),
    time_updated: DateTime.now().toISO(),
    ens_members: null,
    // This should be removed from the schema
    gridCellLayout: { gridColumnStart: 0, gridRowStart: 0, gridRowEnd: 0, gridColumnEnd: 0 },
    legendStyle: energyPlot.legendStyle || null,
    site_id: energyPlot.siteId,
    sub_site_ids: energyPlot.subSiteIds || null,
    forecast_type: energyPlot.forecastType,
    unit: energyPlot.valueUnit ?? ENERGY_FORECAST_VALUE_UNIT,
    datasets:
      energyPlot.groups?.map((group) => {
        return {
          model: group.model,
          plotSeriesStyles: group.plotSeriesStyles,
          init_date: null,
          ens_members: group.ensMembers,
        };
      }) || [],
    title: energyPlot.title || "Title",
    titleStyle: energyPlot.titleStyle ?? null,
    time_lock: energyPlot.timeLock,
    datetime_from: energyPlot.datetimeFrom,
    datetime_from_offset: energyPlot.datetimeFromOffset,
    datetime_to: energyPlot.datetimeTo,
    datetime_to_offset: energyPlot.datetimeToOffset,
    display_time_plotline_visible: !!energyPlot.displayTimeVisible,
    now_time_plotline_visible: !!energyPlot.nowTimeVisible,
  };
};
